/**
 * Task 117 — TEAM-2 PR-2: production-routing FE controls.
 *
 * Mounted inside `ProductionTaskPanel` (see ContentPage.tsx).
 *
 * Renders:
 *   - Always: the current assignee chip ("غير معيّن" when unassigned).
 *   - Conditionally for callers in the locked set (owner /
 *     marketing_manager / production_manager-with-CAT): a
 *     "تعيين / إعادة تعيين" button that opens the routing dialog.
 *
 * The dialog:
 *   - Fetches the eligible-CAT roster via
 *     `list-eligible-production-assignees` (lazy — only when the
 *     dialog opens).
 *   - Renders members grouped by eligibility. Eligible members are
 *     pickable; ineligible members render greyed-out with a tooltip
 *     explaining the gate.
 *   - When `eligible_count === 0`, shows the "no eligible member"
 *     empty state with a CTA to `/app/clients/<clientId>/team`.
 *   - When the user picks a target and clicks confirm, calls
 *     `assign-production-task` with an optional reason. On success,
 *     fires `onAssigned()` so the parent refetches the task; the
 *     assignee chip updates on the next render.
 *
 * Source of truth for the wire contract:
 *   - tasks/111-production-routing-from-client-agency-team.md §5.0
 *   - tasks/117-team2-production-routing-implementation.md
 *   - apps/web/src/api/types.ts :: AssignProductionTaskInput/Data,
 *     ListEligibleProductionAssigneesData, EligibleAssignee,
 *     ASSIGN_PRODUCTION_TASK_CONFLICT_TERMINAL_STATE.
 *
 * Backend NOT deployed yet — Task 117 PR-1 migration is on master but
 * has not been applied to production. This PR-2 wires the UI so the
 * full flow can be activated in one step when the user approves the
 * apply + deploy.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { isApiError } from '../../api/client';
import { assignProductionTask, listEligibleProductionAssignees } from '../../api/endpoints';
import type {
  EligibleAssignee,
  ListEligibleProductionAssigneesData,
  ProductionTaskRow,
} from '../../api/types';
import { ASSIGN_PRODUCTION_TASK_CONFLICT_TERMINAL_STATE } from '../../api/types';
import { LoadingState } from '../../components/states';

interface ProductionRoutingControlsProps {
  readonly task: ProductionTaskRow;
  /**
   * The client this task is scoped to. Passed in so the empty-state
   * CTA can deep-link to `/app/clients/<clientId>/team`. Null when the
   * task is org-scoped (production tasks are content_item-sourced and
   * therefore always have a client_id; this prop is null only when the
   * parent couldn't resolve it — the controls then suppress the CTA).
   */
  readonly clientId: string | null;
  /**
   * The locked-caller gate. TRUE when the actor is `owner` /
   * `marketing_manager` / (`production_manager` AND on the CAT for
   * `clientId`). Server-side `assign_production_task_v1` enforces the
   * SAME gate; this prop hides the action affordance from non-locked
   * callers so they don't see a button that 4xx's at the backend.
   */
  readonly canManageRouting: boolean;
  /**
   * Callback fired after a successful assign or idempotent re-assign.
   * The parent re-runs `list-production-tasks-for-content-item` so the
   * assignee chip and the rest of the panel pick up the new state.
   */
  readonly onAssigned: () => void;
}

type DialogState =
  | { readonly kind: 'closed' }
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'ready';
      readonly data: ListEligibleProductionAssigneesData;
      readonly selectedUserId: string | null;
      readonly reason: string;
    }
  | { readonly kind: 'error'; readonly message: string };

const ARABIC_DISCIPLINE_LABEL: Readonly<Record<string, string>> = {
  content: 'محتوى',
  design: 'تصميم',
  video: 'فيديو',
};

function disciplineLabel(discipline: string): string {
  return ARABIC_DISCIPLINE_LABEL[discipline] ?? discipline;
}

function memberDisplayName(m: EligibleAssignee): string {
  if (m.profile?.full_name && m.profile.full_name.trim().length > 0) {
    return m.profile.full_name.trim();
  }
  if (m.profile?.email) return m.profile.email;
  return m.user_id;
}

function memberRoleSummary(m: EligibleAssignee): string {
  // Show the first agency role in Arabic when available, else the key.
  const r = m.roles[0];
  if (!r) return '';
  return r.name_ar || r.name_en || r.key;
}

function memberIneligibilityHint(m: EligibleAssignee, discipline: string): string {
  if (m.profile && m.profile.is_active === false) {
    return 'الحساب موقوف';
  }
  if (!m.disciplines.includes(discipline as 'content' | 'design' | 'video')) {
    return `لا يملك تخصص ${disciplineLabel(discipline)}`;
  }
  // Fallback when the role-derived disciplines DO include the task's
  // discipline but the server-side gate still rejected the user (e.g.
  // custom-deny on `production.asset.submit` or `task.submit`).
  return 'صلاحيات الإنتاج غير مكتملة لهذا الموظف';
}

function wireErrorToArabic(err: unknown): string {
  if (isApiError(err)) {
    if (err.code === 'conflict') {
      const detail = typeof err.body.detail === 'string' ? err.body.detail : null;
      if (detail === ASSIGN_PRODUCTION_TASK_CONFLICT_TERMINAL_STATE) {
        return 'لا يمكن إعادة تعيين مهمة تم تسليمها أو إنهاؤها أو إلغاؤها.';
      }
      return 'تعذّر إكمال العملية بسبب تعارض في البيانات.';
    }
    if (err.code === 'permission_denied') {
      return 'لا تملك صلاحية تعيين مهام الإنتاج لهذا العميل.';
    }
    if (err.code === 'not_found') {
      return 'المهمة أو الموظف غير متاح.';
    }
    if (err.code === 'validation_error') {
      return 'الموظف غير مؤهل لهذه المهمة. تأكد من أنه عضو في فريق وكالة العميل ولديه صلاحيات الإنتاج.';
    }
    if (err.code === 'unauthenticated') {
      return 'انتهت الجلسة. سيتم إعادة تسجيل الدخول…';
    }
    if (err.code === 'internal_error') {
      return 'حدث خطأ في الخادم. حاول مجدداً بعد قليل.';
    }
  }
  return 'تعذّر إكمال العملية. حاول مجدداً.';
}

const REASON_MAX_LEN = 2000;

export function ProductionAssigneeChip(props: {
  readonly task: ProductionTaskRow;
  readonly assigneeName?: string | null;
}) {
  const { task, assigneeName } = props;
  // Production tasks created via TEAM-2 auto-assign hold
  // `metadata.assignment_source = 'auto_on_handoff'`; manual assigns
  // set `'manual'`. We display the source badge so reviewers can tell
  // system-assigned tasks apart from explicit human ones.
  const metadataSource =
    task.metadata && typeof task.metadata === 'object'
      ? (task.metadata as Record<string, unknown>)['assignment_source']
      : null;
  const sourceLabel =
    metadataSource === 'auto_on_handoff'
      ? 'تعيين تلقائي'
      : metadataSource === 'manual'
        ? 'تعيين يدوي'
        : null;

  if (!task.active_assignee_user_id) {
    if (task.active_ai_worker_id) {
      return (
        <span className="pill pill-info" title="مهمة مُسندة إلى مساعد ذكي">
          مساعد ذكي
        </span>
      );
    }
    return (
      <span className="pill pill-draft" title="لا يوجد عضو فريق معيّن">
        غير معيّن
      </span>
    );
  }

  const displayName = assigneeName ?? task.active_assignee_user_id.slice(0, 8);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span className="pill pill-active">{displayName}</span>
      {sourceLabel ? (
        <span className="tiny" style={{ color: 'var(--muted)' }}>
          {sourceLabel}
        </span>
      ) : null}
    </span>
  );
}

export function ProductionRoutingControls(props: ProductionRoutingControlsProps) {
  const { task, clientId, canManageRouting, onAssigned } = props;
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  const [inFlight, setInFlight] = useState(false);
  const [banner, setBanner] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  // Find the active assignee's profile inside the loaded roster so we
  // can render the chip with a name instead of a user-id slice.
  const activeAssigneeName = useMemo<string | null>(() => {
    if (dialog.kind !== 'ready') return null;
    if (!task.active_assignee_user_id) return null;
    const match = dialog.data.members.find((m) => m.user_id === task.active_assignee_user_id);
    if (!match) return null;
    return match.profile?.full_name ?? match.profile?.email ?? null;
  }, [dialog, task.active_assignee_user_id]);

  const openDialog = useCallback(async () => {
    setBanner(null);
    setDialog({ kind: 'loading' });
    try {
      const data = await listEligibleProductionAssignees({ taskId: task.id });
      setDialog({
        kind: 'ready',
        data,
        selectedUserId: data.active_assignee_user_id,
        reason: '',
      });
    } catch (err) {
      setDialog({ kind: 'error', message: wireErrorToArabic(err) });
    }
  }, [task.id]);

  const closeDialog = useCallback(() => {
    setDialog({ kind: 'closed' });
  }, []);

  const handleConfirm = useCallback(async () => {
    if (dialog.kind !== 'ready') return;
    if (!dialog.selectedUserId) return;
    setInFlight(true);
    setBanner(null);
    try {
      const result = await assignProductionTask({
        input: {
          task_id: task.id,
          assignee_user_id: dialog.selectedUserId,
          reason: dialog.reason.trim().length > 0 ? dialog.reason.trim() : null,
        },
      });
      const successText = result.already_assigned
        ? 'الموظف معيّن بالفعل على هذه المهمة.'
        : result.previous_assignee_user_id
          ? 'تم إعادة تعيين المهمة بنجاح.'
          : 'تم تعيين المهمة بنجاح.';
      setBanner({ kind: 'success', text: successText });
      setDialog({ kind: 'closed' });
      onAssigned();
    } catch (err) {
      setBanner({ kind: 'error', text: wireErrorToArabic(err) });
    } finally {
      setInFlight(false);
    }
  }, [dialog, task.id, onAssigned]);

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span className="lbl">المُعيَّن:</span>
        <ProductionAssigneeChip task={task} assigneeName={activeAssigneeName} />
        {canManageRouting ? (
          <button
            type="button"
            className="btn btn-secondary"
            style={{ marginInlineStart: 'auto' }}
            disabled={dialog.kind === 'loading' || inFlight}
            onClick={() => {
              if (dialog.kind === 'closed' || dialog.kind === 'error') {
                void openDialog();
              }
            }}
          >
            {task.active_assignee_user_id ? 'إعادة التعيين' : 'تعيين موظف'}
          </button>
        ) : null}
      </div>

      {banner ? (
        <div
          className={`banner ${banner.kind === 'success' ? 'banner-success' : 'banner-error'}`}
          role={banner.kind === 'success' ? 'status' : 'alert'}
          style={{ marginTop: 10 }}
        >
          {banner.text}
        </div>
      ) : null}

      {dialog.kind !== 'closed' ? (
        <ProductionRoutingDialog
          dialog={dialog}
          inFlight={inFlight}
          clientId={clientId}
          onSelectUserId={(userId) => {
            if (dialog.kind === 'ready') {
              setDialog({ ...dialog, selectedUserId: userId });
            }
          }}
          onChangeReason={(reason) => {
            if (dialog.kind === 'ready') {
              setDialog({ ...dialog, reason });
            }
          }}
          onClose={closeDialog}
          onConfirm={handleConfirm}
        />
      ) : null}
    </div>
  );
}

interface ProductionRoutingDialogProps {
  readonly dialog: DialogState;
  readonly inFlight: boolean;
  readonly clientId: string | null;
  readonly onSelectUserId: (userId: string) => void;
  readonly onChangeReason: (reason: string) => void;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
}

function ProductionRoutingDialog(props: ProductionRoutingDialogProps) {
  const { dialog, inFlight, clientId, onSelectUserId, onChangeReason, onClose, onConfirm } = props;

  // Close on Escape, mirroring other modals in this app shell.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !inFlight) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, inFlight]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="تعيين مهمة الإنتاج"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: 16,
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: 560,
          width: '100%',
          maxHeight: '85vh',
          overflow: 'auto',
          padding: 24,
        }}
      >
        <div className="sec-title">
          <h2>تعيين مهمة الإنتاج</h2>
        </div>

        {dialog.kind === 'loading' ? <LoadingState /> : null}

        {dialog.kind === 'error' ? (
          <div className="banner banner-error" role="alert" style={{ marginTop: 12 }}>
            {dialog.message}
          </div>
        ) : null}

        {dialog.kind === 'ready' ? (
          <ProductionRoutingDialogBody
            data={dialog.data}
            selectedUserId={dialog.selectedUserId}
            reason={dialog.reason}
            clientId={clientId}
            onSelectUserId={onSelectUserId}
            onChangeReason={onChangeReason}
          />
        ) : null}

        <div
          style={{
            marginTop: 18,
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
          }}
        >
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={inFlight}>
            إلغاء
          </button>
          {dialog.kind === 'ready' && dialog.data.eligible_count > 0 ? (
            <button
              type="button"
              className="btn"
              onClick={onConfirm}
              disabled={inFlight || !dialog.selectedUserId}
            >
              {inFlight ? 'جارٍ التعيين…' : 'تأكيد التعيين'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ProductionRoutingDialogBody(props: {
  readonly data: ListEligibleProductionAssigneesData;
  readonly selectedUserId: string | null;
  readonly reason: string;
  readonly clientId: string | null;
  readonly onSelectUserId: (userId: string) => void;
  readonly onChangeReason: (reason: string) => void;
}) {
  const { data, selectedUserId, reason, clientId, onSelectUserId, onChangeReason } = props;
  const eligible = data.members.filter((m) => m.is_eligible_for_this_task);
  const ineligible = data.members.filter((m) => !m.is_eligible_for_this_task);
  const teamLink = clientId ? `/app/clients/${clientId}/team` : null;

  if (data.eligible_count === 0) {
    return (
      <div style={{ marginTop: 12 }}>
        <p style={{ marginBottom: 8 }}>
          لا يوجد عضو فريق وكالة مؤهل لتخصص <strong>{disciplineLabel(data.discipline)}</strong> على
          هذا العميل.
        </p>
        <p className="tiny" style={{ color: 'var(--muted)' }}>
          الأدوار المؤهلة لهذا التخصص:{' '}
          {(() => {
            switch (data.discipline) {
              case 'content':
                return 'كاتب محتوى / كاتب محتوى أول';
              case 'design':
                return 'مصمم / مصمم أول';
              case 'video':
                return 'مونتير فيديو / مصوّر / مصمم أول';
              default:
                return data.discipline;
            }
          })()}
        </p>
        {teamLink ? (
          <a className="btn" href={teamLink} style={{ marginTop: 12, display: 'inline-block' }}>
            أضف عضوًا مؤهلاً لفريق وكالة العميل
          </a>
        ) : null}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12 }}>
      <p className="tiny" style={{ marginBottom: 8, color: 'var(--muted)' }}>
        تخصص المهمة: <strong>{disciplineLabel(data.discipline)}</strong>. اختر عضوًا مؤهلًا من فريق
        وكالة العميل.
      </p>
      <ul className="row-list" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {eligible.map((m) => (
          <li key={m.user_id}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                border:
                  selectedUserId === m.user_id
                    ? '2px solid var(--accent)'
                    : '1px solid var(--border)',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name="production-routing-target"
                checked={selectedUserId === m.user_id}
                onChange={() => onSelectUserId(m.user_id)}
              />
              <div style={{ flex: 1 }}>
                <div>{memberDisplayName(m)}</div>
                <div className="tiny" style={{ color: 'var(--muted)' }}>
                  {memberRoleSummary(m)}
                  {m.user_id === data.active_assignee_user_id ? ' · المُعيَّن حاليًا' : ''}
                </div>
              </div>
            </label>
          </li>
        ))}
      </ul>

      {ineligible.length > 0 ? (
        <>
          <p className="tiny" style={{ marginTop: 14, marginBottom: 6, color: 'var(--muted)' }}>
            أعضاء غير مؤهلين لهذه المهمة:
          </p>
          <ul className="row-list" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {ineligible.map((m) => (
              <li
                key={m.user_id}
                title={memberIneligibilityHint(m, data.discipline)}
                style={{
                  padding: '6px 10px',
                  color: 'var(--muted)',
                  fontSize: 13,
                }}
              >
                {memberDisplayName(m)} —{' '}
                <span className="tiny">{memberIneligibilityHint(m, data.discipline)}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <label
        style={{
          marginTop: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <span className="lbl">سبب التعيين (اختياري — حتى {REASON_MAX_LEN} حرف)</span>
        <textarea
          rows={2}
          value={reason}
          maxLength={REASON_MAX_LEN}
          onChange={(e) => onChangeReason(e.target.value)}
          placeholder="مثال: عميل عمر مشغول هذا الأسبوع."
        />
      </label>
    </div>
  );
}
